import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cancelSelfieUpload, createSelfieUploadTarget, putToUploadUrl } from '../api/client';
import type { AuthState } from '../api/request';
import { byteLength, sha256Hex } from '../lib/checksum';
import {
  SELFIE_IDLE,
  SELFIE_INITIAL_JPEG_QUALITY,
  advanceSelfie,
  isSelfieBusy,
  isSelfieUploaded,
  markSelfieFailed,
  nextCompressionQuality,
  retrySelfie,
  scaledSelfieDimensions,
  withSelfieProgress,
  type SelfieSlot,
} from '../lib/selfie-capture';

export interface SelfieValue {
  readonly selfieFileId: string | null;
  readonly noSelfieReason: string;
}

export const SELFIE_VALUE_EMPTY: SelfieValue = { selfieFileId: null, noSelfieReason: '' };

/**
 * Kompresi sisi klien (item 9, review 12 Sep 2026, spec Tahap 2 §9): sisi panjang 640px, JPEG
 * mulai kualitas 0.7 diturunkan bertahap sampai di bawah target atau kualitas terendah yang
 * layak (`nextCompressionQuality`) — kebijakan tangganya MURNI dan teruji di `selfie-capture.ts`;
 * fungsi ini hanya mekanismenya (canvas sungguhan), karena itu diverifikasi lewat pengujian
 * browser manual, bukan `node --test`.
 */
async function compressToJpeg(source: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(source);
  try {
    const { width, height } = scaledSelfieDimensions(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Kanvas kompresi tidak tersedia di perangkat ini.');
    }
    ctx.drawImage(bitmap, 0, 0, width, height);

    // Tangga kualitas SENGAJA berurutan: setiap percobaan butuh ukuran hasil percobaan
    // sebelumnya (`nextCompressionQuality`) untuk memutuskan langkah berikutnya.
    let quality: number | null = SELFIE_INITIAL_JPEG_QUALITY;
    let blob: Blob | null = null;
    do {
      blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality ?? undefined),
      );
      if (!blob) {
        throw new Error('Gagal memampatkan foto.');
      }
      quality = nextCompressionQuality(quality, blob.size);
    } while (quality !== null);
    return blob;
  } finally {
    bitmap.close();
  }
}

function phaseLabel(slot: SelfieSlot): string {
  switch (slot.phase) {
    case 'compressing':
      return 'Memampatkan foto…';
    case 'hashing':
      return 'Memeriksa berkas…';
    case 'requesting-target':
      return 'Menyiapkan unggahan…';
    case 'uploading':
      return `Mengunggah ${slot.progress}%`;
    default:
      return '';
  }
}

/**
 * Swafoto saat absen (item 9, review 12 Sep 2026, K3) — dipakai IDENTIK untuk absen masuk dan
 * pulang. Menjaga DUA jalan yang sama-sama sah bagi server (`SelfieRequiredError`,
 * `attendance.service.ts`): unggah selfie SUNGGUHAN, atau tulis alasan saat kamera gagal.
 *
 * `required` hanya mengubah tampilan (label "wajib", fallback alasan muncul) — server tetap
 * satu-satunya yang menegakkan `selfiePolicy`.
 */
export function SelfieCapture({
  auth,
  required,
  value,
  onChange,
  disabled,
}: {
  readonly auth: AuthState;
  readonly required: boolean;
  readonly value: SelfieValue;
  readonly onChange: (value: SelfieValue) => void;
  readonly disabled?: boolean;
}): ReactNode {
  const [slot, setSlot] = useState<SelfieSlot>(SELFIE_IDLE);
  const [preview, setPreview] = useState<string | null>(null);
  const [showFallback, setShowFallback] = useState(false);
  const previewRef = useRef<string | null>(null);

  // Pratinjau adalah URL objek browser — harus dilepas, atau tiap jepretan baru membocorkan
  // memori foto sebelumnya selama layar masih terbuka.
  useEffect(() => {
    return () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  async function handleFile(file: File): Promise<void> {
    setSlot(advanceSelfie(SELFIE_IDLE, 'compressing'));
    try {
      const compressed = await compressToJpeg(file);
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
      previewRef.current = URL.createObjectURL(compressed);
      setPreview(previewRef.current);

      setSlot((s) => advanceSelfie(s, 'hashing', { compressedBytes: compressed.size }));
      const bytes = new Uint8Array(await compressed.arrayBuffer());
      const checksum = await sha256Hex(bytes);

      setSlot((s) => advanceSelfie(s, 'requesting-target'));
      const target = await createSelfieUploadTarget(auth, {
        mimeType: 'image/jpeg',
        // Ukuran dan checksum dari SUMBER YANG SAMA (byte hasil kompresi) — bukan `file.size`
        // yang merujuk berkas SEBELUM diproses.
        fileSizeBytes: byteLength(bytes),
        checksumSha256: checksum,
      });

      setSlot((s) => advanceSelfie(s, 'uploading', { fileId: target.fileId }));
      await putToUploadUrl(target.uploadUrl, compressed, (percent) =>
        setSlot((s) => withSelfieProgress(s, percent)),
      );
      setSlot((s) => advanceSelfie(s, 'uploaded'));
      onChange({ selfieFileId: target.fileId, noSelfieReason: '' });
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Gagal mengambil selfie.';
      setSlot((s) => markSelfieFailed(s, message));
      onChange({ ...value, selfieFileId: null });
    }
  }

  function retake(): void {
    // Berkas lama TIDAK dipakai ulang — target unggah punya masa berlaku, dan membatalkannya
    // mencegah berkas yatim yang hanya bisa dibersihkan orphan cleanup.
    if (slot.fileId) void cancelSelfieUpload(auth, slot.fileId).catch(() => {});
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
    }
    setPreview(null);
    setSlot(retrySelfie());
    onChange({ selfieFileId: null, noSelfieReason: value.noSelfieReason });
  }

  const busy = isSelfieBusy(slot);
  const uploaded = isSelfieUploaded(slot);

  return (
    <div className="selfie-capture">
      <label htmlFor="selfie">Swafoto{required ? ' (wajib)' : ' (opsional)'}</label>

      {uploaded && preview ? (
        <div className="selfie-preview-row">
          <img src={preview} alt="Pratinjau swafoto" className="selfie-preview" />
          <button type="button" onClick={retake} disabled={disabled}>
            Ambil ulang
          </button>
        </div>
      ) : (
        <input
          id="selfie"
          type="file"
          accept="image/*"
          capture="user"
          disabled={disabled || busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void handleFile(file);
          }}
        />
      )}

      {busy ? <p className="muted small">{phaseLabel(slot)}</p> : null}
      {slot.phase === 'failed' ? (
        <p className="warning small" role="alert">
          {slot.errorMessage}
        </p>
      ) : null}

      {/*
        Jalan keluar kamera gagal — hanya relevan saat selfie memang WAJIB dan belum ada yang
        terunggah. Kebijakan OPTIONAL/DISABLED tidak pernah butuh alasan (`selfieSatisfied`).
      */}
      {required && !uploaded ? (
        <>
          <button
            type="button"
            className="tautan-kecil"
            onClick={() => setShowFallback((v) => !v)}
            disabled={disabled}
          >
            {showFallback ? 'Sembunyikan alasan' : 'Kamera tidak berfungsi?'}
          </button>
          {showFallback ? (
            <>
              <label htmlFor="noSelfieReason">Alasan tanpa swafoto</label>
              <textarea
                id="noSelfieReason"
                value={value.noSelfieReason}
                onChange={(e) => onChange({ selfieFileId: null, noSelfieReason: e.target.value })}
                maxLength={500}
                rows={2}
                placeholder="mis. Kamera rusak"
                disabled={disabled}
              />
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
